import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  getActivitiesById,
  handleCreateActivities,
  handleUpdateActivities,
} from "../../../API/Mediatheque/ActivityReports/activityService";
import BreadCrumb from "../../breadcrumb";
import Header from "../../DashboardNavigation/header";
import Sidebar from "../../DashboardNavigation/sidebar";
import PdfFileUpload from "../../FileUpload/PdfFileUpload";
import { Button } from "../../ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../ui/form";
import { Heading } from "../../ui/heading";
import { Input } from "../../ui/input";
import { Separator } from "../../ui/separator";
import {
  ActivityFormValues,
  activitySchema,
  UpdatedActivityFormValues,
  updatedActivitySchema,
} from "./activity-zod-schema";

export const ActivityReportForm: React.ComponentType<any> = () => {
  const router = useNavigate();
  const uploadUrl = process.env.REACT_APP_FILE_UPLOAD;

  const { id } = useParams();
  const initialData = id ? parseInt(id) : undefined;

  const [loading, setLoading] = useState(false);
  const title = initialData ? "Modifier le fichier" : "Ajouter un fichier";
  const description = initialData
    ? "Modifier le fichier"
    : "Ajouter un nouveau fichier";

  const action = initialData ? "Mise à jour" : "Ajouter";

  const form = useForm<ActivityFormValues | UpdatedActivityFormValues>({
    resolver: zodResolver(initialData ? updatedActivitySchema : activitySchema),
  });

  const onSubmit = async () => {
    setLoading(true);
    const formData = form.getValues();
    try {
      if (initialData) {
        await handleUpdateActivities(initialData, formData, router);
      } else {
        await handleCreateActivities(formData, router);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const breadcrumbItems = [
    {
      title: "Rapports d'activité",
      link: "/admin-dashboard/mediatheque/rapports-activites",
    },
    {
      title: initialData ? "Mise à jour" : "Ajouter",
      link: "/admin-dashboard/mediatheque/rapports-activites/ajouter",
    },
  ];

  const [data, setData] = useState<any>();
  useEffect(() => {
    const fetchData = async () => {
      if (initialData) {
        const activityData = await getActivitiesById(initialData);

        setData(activityData);
        form.reset({
          title: activityData.title,

          file: activityData.file
            ? {
                fileName: activityData.file,
                name: activityData.file,
                fileSize: 0,
                size: 0,
                key: activityData.file,
              }
            : undefined,
        });
      }
    };

    if (initialData) {
      fetchData();
    }
  }, [initialData]);

  return (
    <>
      <Header />
      <div className="flex h-screen overflow-hidden">
        <Sidebar />
        <div className="w-full p-10">
          <BreadCrumb items={breadcrumbItems} />
          <div className="flex items-center justify-between">
            <Heading title={title} description={description} />
          </div>
          <Separator />
          <Form {...form}>
            <form
              className="w-full space-y-8"
              onSubmit={form.handleSubmit(onSubmit)}
            >
              <div className="flex justify-between">
                <div className="w-1/2">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Titre </FormLabel>
                        <FormControl>
                          <Input
                            disabled={loading}
                            placeholder={"Titre..."}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {data?.file && (
                <div className="mt-4 mb-4">
                  <a
                    href={`${uploadUrl}/medias/reports/${data.file}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 underline"
                  >
                    Consulter l'ancien PDF
                  </a>
                </div>
              )}
              <FormField
                control={form.control}
                name="file"
                render={() => (
                  <FormItem>
                    <FormControl>
                      <PdfFileUpload
                        onChange={(files) => {
                          const file = files[0];
                          if (file) {
                            const mappedFile = {
                              key: file.id,
                              fileName: file.fileName,
                              name: file.fileName,
                              fileSize: parseInt(file.fileSize),
                              size: parseInt(file.fileSize),
                              file: file.file,
                            };
                            form.setValue("file", mappedFile);
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex space-x-5">
                <div>
                  <Button
                    disabled={loading}
                    isLoading={loading}
                    className="ml-auto"
                    type="submit"
                  >
                    {action}
                  </Button>
                </div>
                <div>
                  <Button
                    onClick={() => router(-1)}
                    disabled={loading}
                    isLoading={loading}
                    className="ml-auto bg-red-500"
                    type="button"
                  >
                    {"Annuler"}
                  </Button>
                </div>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </>
  );
};
